import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ChevronDown,
  ChevronRight,
  Lock,
  MoreHorizontal,
  PanelLeftClose,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  selectCurrentWorkspace,
  selectUnfiledTopics,
  setWorkspaceLoaded,
} from '../../store/workspacesSlice';
import { getWorkspaceDetailPath } from '../../lib/resolveMostRecentWorkspace';
import { renameWorkspace } from '../../lib/workspaceActions';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { usePremium } from '../Subscription/PremiumProvider';
import LearnSidebarStats from './LearnSidebarStats';
import {
  MASTERY_FILTER_OPTIONS,
  MASTERY_FILTERS,
  countBasketsByMastery,
} from './masteryFilter';

/** Hide import counts in Public workspaces meta until they are meaningfully large. */
const IMPORTS_COUNT_MIN = 25;

/**
 * Shared LEARN sidebar for Connections.
 * Workspace list / public templates / mutators come from LearnLayout (single source).
 */
const LearnSidebar = ({
  workspaceId,
  workspaceName,
  masteryFilter,
  onMasteryFilterChange,
  onNewWorkspace,
  onBasketsMasteryClick,
  onUnfiledRemainingClick,
  onCloseMobile,
  onCollapse,
  workspaces = [],
  renameInList,
  publicWorkspaces = [],
  importedOriginalIds,
  onRequestDuplicate,
  onRequestDelete,
  onImportPublic,
  importingWorkspaceId,
  bordered = true,
  className = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isPremium, isLoading, openUpgradeModal } = usePremium();
  const { baskets, topics } = useSelector(selectCurrentWorkspace);
  const unfiledTopics = useSelector(selectUnfiledTopics);
  const isPoorLocked = !isLoading && !isPremium();
  const isDuplicateLocked = !isLoading && !isPremium();
  const masteryPremiumOpts = useMemo(
    () => ({ isPremium, isLoading }),
    [isPremium, isLoading]
  );
  const masteryCounts = useMemo(
    () => countBasketsByMastery(baskets || [], masteryPremiumOpts),
    [baskets, masteryPremiumOpts]
  );

  const [isStatsOpen, setIsStatsOpen] = useState(true);
  const [isMasteryOpen, setIsMasteryOpen] = useState(false);
  const [isPublicWorkspacesOpen, setIsPublicWorkspacesOpen] = useState(true);
  const [isYourWorkspacesOpen, setIsYourWorkspacesOpen] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const renameInputRef = useRef(null);
  const originalNameRef = useRef('');
  const isCommittingRef = useRef(false);

  useEffect(() => {
    if (editingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingId]);

  // Keep the Mastery section open while a filter is active so the row stays visible.
  useEffect(() => {
    if (masteryFilter != null) {
      setIsMasteryOpen(true);
    }
  }, [masteryFilter]);

  const handleToggleStats = () => {
    setIsStatsOpen((prev) => !prev);
  };

  const handleToggleMastery = () => {
    setIsMasteryOpen((prev) => !prev);
  };

  const handleTogglePublicWorkspaces = () => {
    setIsPublicWorkspacesOpen((prev) => !prev);
  };

  const handleToggleYourWorkspaces = () => {
    setIsYourWorkspacesOpen((prev) => !prev);
  };

  const handleMasterySelect = (filterId) => {
    if (filterId === MASTERY_FILTERS.POOR && isPoorLocked) {
      captureConnectionsEvent('mastery_filter_locked_tapped', { bucket: 'poor' });
      openUpgradeModal('mastery_poor');
      return;
    }

    const isActive = masteryFilter === filterId;
    const count = masteryCounts[filterId] ?? 0;
    // Zero inactive rows are dead ends (locked Poor already returned above).
    if (count === 0 && !isActive) {
      return;
    }

    if (isActive) {
      captureConnectionsEvent('mastery_filter_cleared', { source: 'sidebar' });
      onMasteryFilterChange(null);
      return;
    }

    captureConnectionsEvent('mastery_filter_applied', { bucket: filterId });
    onMasteryFilterChange(filterId);
  };

  const handleDuplicateClick = (workspace) => {
    if (isDuplicateLocked) {
      openUpgradeModal('duplicate');
      return;
    }
    onRequestDuplicate?.(workspace);
  };

  const handleWorkspaceSwitch = (workspace) => {
    if (!workspace?.id || workspace.id === workspaceId) {
      onCloseMobile?.();
      return;
    }

    dispatch(setWorkspaceLoaded(false));
    navigate(getWorkspaceDetailPath(workspace), {
      state: {
        workspaceName: workspace.name,
        workspaceOpenSource: 'manual_switch',
      },
    });
    onCloseMobile?.();
  };

  const handleNewWorkspace = () => {
    onNewWorkspace?.();
    onCloseMobile?.();
  };

  const handleStartRename = (workspace) => {
    isCommittingRef.current = false;
    setEditingId(workspace.id);
    setEditedName(workspace.name);
    originalNameRef.current = workspace.name;
  };

  const handleCancelRename = () => {
    if (isCommittingRef.current) return;
    isCommittingRef.current = true;
    setEditingId(null);
    setEditedName('');
    originalNameRef.current = '';
  };

  const handleSaveRename = async () => {
    if (isCommittingRef.current || !editingId) return;
    isCommittingRef.current = true;

    const trimmed = editedName.trim();
    const original = originalNameRef.current;

    if (!trimmed || trimmed === original) {
      setEditingId(null);
      setEditedName('');
      originalNameRef.current = '';
      isCommittingRef.current = false;
      return;
    }

    const id = editingId;
    renameInList?.(id, trimmed);
    setEditingId(null);
    setEditedName('');

    try {
      await renameWorkspace({ workspaceId: id, name: trimmed });
      captureConnectionsEvent('workspace_renamed', {
        workspace_id: id,
        source: 'sidebar',
      });

      if (id === workspaceId) {
        navigate(location.pathname, {
          replace: true,
          state: {
            ...location.state,
            workspaceName: trimmed,
          },
        });
      }
    } catch (error) {
      console.error('Error renaming workspace:', error);
      renameInList?.(id, original);
      toast.error('Failed to rename workspace. Please try again.');
    } finally {
      isCommittingRef.current = false;
    }
  };

  const handleRenameKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveRename();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelRename();
    }
  };

  const connectionsPath = `/workspace/${workspaceId}`;
  const importedSet = importedOriginalIds || new Set();

  return (
    <aside
      className={`flex flex-col h-full bg-white ${bordered ? 'border-r border-neutral-200' : ''} ${className}`}
      aria-label="Learn navigation"
    >
      <div className="flex items-center justify-between px-4 py-3 md:hidden border-b border-neutral-100">
        <span className="text-sm font-semibold text-neutral-900 truncate">
          {workspaceName || 'Workspace'}
        </span>
        {onCloseMobile && (
          <button
            type="button"
            onClick={onCloseMobile}
            className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 focus:outline-none focus:ring-1 focus:ring-neutral-400"
            aria-label="Close sidebar"
          >
            <X size={18} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="hidden md:flex h-14 sm:h-16 shrink-0 items-center justify-between gap-2 border-b border-neutral-200 px-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Learn
        </p>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            className="p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-cranberry-700"
            aria-label="Collapse sidebar"
            aria-expanded="true"
            aria-controls="learn-sidebar"
          >
            <PanelLeftClose size={20} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 mb-2 md:hidden">
            Learn
          </p>
          <nav className="flex flex-col gap-0.5" aria-label="Learn">
            <NavLink
              to={connectionsPath}
              end
              state={{ workspaceName }}
              onClick={onCloseMobile}
              className={({ isActive }) =>
                `px-2 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-neutral-400 ${
                  isActive
                    ? 'font-semibold text-cranberry-700 bg-cranberry-50'
                    : 'font-medium text-neutral-600 hover:bg-neutral-50'
                }`
              }
            >
              Connections
            </NavLink>
          </nav>
        </div>

        <div>
          <button
            type="button"
            onClick={handleToggleStats}
            className="flex items-center gap-1.5 w-full text-left focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded"
            aria-expanded={isStatsOpen}
          >
            {isStatsOpen ? (
              <ChevronDown size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronRight size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
              Stats
            </span>
          </button>

          {isStatsOpen && (
            <div className="mt-2">
              <LearnSidebarStats
                baskets={baskets || []}
                topics={topics || []}
                unfiledCount={(unfiledTopics || []).length}
                onBasketsMasteryClick={() => {
                  onBasketsMasteryClick?.();
                  onCloseMobile?.();
                }}
                onUnfiledRemainingClick={() => {
                  onUnfiledRemainingClick?.();
                  onCloseMobile?.();
                }}
              />
            </div>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={handleToggleMastery}
            className="flex items-center gap-1.5 w-full text-left focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded"
            aria-expanded={isMasteryOpen}
          >
            {isMasteryOpen ? (
              <ChevronDown size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronRight size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
              Mastery filter
            </span>
          </button>

          {isMasteryOpen && (
            <div className="mt-2 flex flex-col gap-0.5" role="group" aria-label="Mastery filter">
              {MASTERY_FILTER_OPTIONS.map((option) => {
                const isActive = masteryFilter === option.id;
                const isLocked =
                  option.id === MASTERY_FILTERS.POOR && isPoorLocked;
                const count = masteryCounts[option.id] ?? 0;
                const isZeroDead = count === 0 && !isActive && !isLocked;
                const countPhrase = `${count} basket${count === 1 ? '' : 's'}`;
                const ariaLabel = isLocked
                  ? `${option.label}, ${countPhrase} (Premium)`
                  : `${option.label}, ${countPhrase}`;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleMasterySelect(option.id)}
                    className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg text-sm text-left focus:outline-none focus:ring-1 focus:ring-neutral-400 ${
                      isActive
                        ? 'font-semibold text-cranberry-700 bg-cranberry-50'
                        : isZeroDead || isLocked
                          ? 'font-medium text-neutral-400 hover:bg-neutral-50'
                          : 'font-medium text-neutral-600 hover:bg-neutral-50'
                    } ${isZeroDead ? 'cursor-default' : ''}`}
                    aria-pressed={isActive}
                    aria-disabled={isZeroDead ? 'true' : undefined}
                    aria-label={ariaLabel}
                  >
                    <span>{option.label}</span>
                    <span className="inline-flex items-center gap-1.5 shrink-0">
                      <span className="text-xs tabular-nums text-neutral-400">
                        {count}
                      </span>
                      {isLocked && (
                        <Lock
                          size={14}
                          className="text-neutral-400 shrink-0"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {publicWorkspaces.length > 0 && (
          <div>
            <button
              type="button"
              onClick={handleTogglePublicWorkspaces}
              className="flex items-center gap-1.5 w-full text-left focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded"
              aria-expanded={isPublicWorkspacesOpen}
            >
              {isPublicWorkspacesOpen ? (
                <ChevronDown size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
              ) : (
                <ChevronRight size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
              )}
              <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Public workspaces
              </span>
            </button>

            {isPublicWorkspacesOpen && (
              <ul className="mt-2 space-y-2 px-2">
                {publicWorkspaces.map((workspace) => {
                  const isImported = importedSet.has(workspace.id);
                  const isImportingThis = importingWorkspaceId === workspace.id;
                  const anyImporting = Boolean(importingWorkspaceId);
                  const metaParts = [];
                  if (workspace.basketCount > 0) {
                    metaParts.push(
                      `${workspace.basketCount} basket${workspace.basketCount === 1 ? '' : 's'}`
                    );
                  }
                  metaParts.push(
                    `${workspace.topicCount} topic${workspace.topicCount === 1 ? '' : 's'}`
                  );
                  if ((workspace.downloadCount || 0) >= IMPORTS_COUNT_MIN) {
                    metaParts.push(`${workspace.downloadCount} imports`);
                  }

                  return (
                    <li key={workspace.id} className="flex items-start gap-2 min-w-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-neutral-800 truncate">
                          {workspace.name}
                        </p>
                        <p className="text-[11px] text-neutral-400 leading-snug">
                          {metaParts.join(' · ')}
                        </p>
                      </div>
                      {isImported ? (
                        <span className="shrink-0 mt-0.5 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
                          Added
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onImportPublic?.(workspace)}
                          disabled={anyImporting}
                          className="shrink-0 mt-0.5 flex h-7 w-7 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 hover:border-neutral-500 hover:bg-neutral-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Add ${workspace.name} to your workspaces`}
                        >
                          {isImportingThis ? (
                            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                          ) : (
                            <Plus size={14} aria-hidden="true" />
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={handleToggleYourWorkspaces}
            className="flex items-center gap-1.5 w-full text-left focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded"
            aria-expanded={isYourWorkspacesOpen}
          >
            {isYourWorkspacesOpen ? (
              <ChevronDown size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronRight size={14} className="text-neutral-400 shrink-0" aria-hidden="true" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
              Your workspaces
            </span>
          </button>

          {isYourWorkspacesOpen && (
            <>
              <ul className="mt-2 space-y-0.5">
                {workspaces.map((workspace) => {
                  const isCurrent = workspace.id === workspaceId;
                  const isEditing = editingId === workspace.id;

                  return (
                    <li
                      key={workspace.id}
                      className={`group relative rounded-lg ${
                        isCurrent ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                      }`}
                    >
                      {isEditing ? (
                        <input
                          ref={renameInputRef}
                          type="text"
                          value={editedName}
                          onChange={(event) => setEditedName(event.target.value)}
                          onKeyDown={handleRenameKeyDown}
                          onBlur={handleSaveRename}
                          className="w-full px-2 py-1.5 text-sm rounded-lg border-2 border-neutral-900 outline-none"
                          aria-label={`Rename ${workspace.name}`}
                        />
                      ) : (
                        <div className="flex items-center min-w-0">
                          <button
                            type="button"
                            onClick={() => handleWorkspaceSwitch(workspace)}
                            className={`min-w-0 flex-1 px-2 py-1.5 rounded-lg text-sm text-left truncate focus:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400 ${
                              isCurrent
                                ? 'font-semibold text-neutral-900'
                                : 'font-normal text-neutral-500 group-hover:text-neutral-700'
                            }`}
                            aria-current={isCurrent ? 'page' : undefined}
                          >
                            {workspace.name}
                          </button>

                          <Menu as="div" className="relative shrink-0">
                            <MenuButton
                              className={`p-1 rounded-md text-neutral-500 hover:bg-neutral-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400 data-[open]:opacity-100 ${
                                isCurrent
                                  ? 'opacity-100'
                                  : 'opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100'
                              }`}
                              aria-label={`More actions for ${workspace.name}`}
                              aria-haspopup="menu"
                            >
                              <MoreHorizontal size={16} aria-hidden="true" />
                            </MenuButton>
                            <MenuItems
                              anchor="bottom end"
                              className="z-50 w-40 origin-top-right rounded-xl border-2 border-neutral-900 bg-white py-1 shadow-lg focus:outline-none"
                            >
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleStartRename(workspace)}
                                    className={`block w-full px-3 py-2 text-left text-sm ${
                                      focus ? 'bg-neutral-100' : ''
                                    } text-neutral-900`}
                                  >
                                    Rename
                                  </button>
                                )}
                              </MenuItem>
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleDuplicateClick(workspace)}
                                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                                      focus ? 'bg-neutral-100' : ''
                                    } text-neutral-900`}
                                  >
                                    <span>Duplicate</span>
                                    {isDuplicateLocked && (
                                      <Lock
                                        size={14}
                                        className="text-neutral-400 shrink-0"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </button>
                                )}
                              </MenuItem>
                              <div className="my-1 border-t border-neutral-200" role="separator" />
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    onClick={() => onRequestDelete?.(workspace)}
                                    className={`block w-full px-3 py-2 text-left text-sm ${
                                      focus ? 'bg-neutral-100' : ''
                                    } text-cranberry-700`}
                                  >
                                    Delete
                                  </button>
                                )}
                              </MenuItem>
                            </MenuItems>
                          </Menu>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                onClick={handleNewWorkspace}
                className="mt-2 text-sm text-cranberry-700 hover:text-cranberry-600 font-medium focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded px-1"
              >
                + New workspace
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default LearnSidebar;
